import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiService } from "../services/api";
import {
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

interface SubscriptionStatus {
  status: "active" | "trialing" | "past_due" | "canceled";
  paymentFailedAt: string | null;
  gracePeriodEnds: string | null;
  gracePeriodActive: boolean;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  planName: string | null;
  planPriceCents: number | null;
}

interface UsageInfo {
  monthly_minute_limit: number;
  seconds_used_current_period: number;
  current_period_start: string;
  current_period_end: string;
  remaining_seconds: number;
  usage_percentage: number;
}

const AccountManagement: React.FC = () => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);

  useEffect(() => {
    loadAccountData();
  }, []);

  const loadAccountData = async () => {
    try {
      const [statusResponse, usageResponse] = await Promise.all([
        apiService.getSubscriptionStatus(),
        apiService.getCompanyUsageStats(),
      ]);

      if (statusResponse.success && statusResponse.data) {
        setSubscriptionStatus(statusResponse.data as SubscriptionStatus);
      }

      if (usageResponse.success && usageResponse.data) {
        setUsageInfo(usageResponse.data as UsageInfo);
      }
    } catch (error) {
      console.error("Failed to load account data:", error);
      toast.error("Failed to load account information");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      const response = await apiService.createCustomerPortalSession();

      if (response.success && response.data?.url) {
        // Redirect to Stripe Customer Portal
        window.open(response.data.url, "_blank");
      } else {
        toast.error("Failed to open payment portal");
      }
    } catch (error) {
      console.error("Portal error:", error);
      toast.error("Failed to open payment portal");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getTrialDaysRemaining = () => {
    if (!subscriptionStatus?.currentPeriodEnd) return null;
    const end = new Date(subscriptionStatus.currentPeriodEnd);
    const now = new Date();
    const diff = Math.ceil(
      (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
    return Math.max(0, diff);
  };

  const getStatusBadge = () => {
    if (!subscriptionStatus) return null;

    switch (subscriptionStatus.status) {
      case "trialing": {
        const daysLeft = getTrialDaysRemaining();
        if (subscriptionStatus.cancelAtPeriodEnd) {
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
              <XCircleIcon className="h-4 w-4 mr-1" />
              Trial Canceled
              {daysLeft !== null && daysLeft > 0 && (
                <span className="ml-1">· access until {formatDate(subscriptionStatus.currentPeriodEnd)}</span>
              )}
            </span>
          );
        }
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <BeakerIcon className="h-4 w-4 mr-1" />
            Free Trial
            {daysLeft !== null && (
              <span className="ml-1">· {daysLeft}d left</span>
            )}
          </span>
        );
      }
      case "active":
        if (subscriptionStatus.cancelAtPeriodEnd) {
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
              Canceling
            </span>
          );
        }
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Active
          </span>
        );
      case "past_due":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
            Past Due
          </span>
        );
      case "canceled":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-4 w-4 mr-1" />
            Canceled
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Account Management</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your subscription and billing information
        </p>
      </div>

      {/* Subscription Status Card */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Subscription Status
            </h2>
            {getStatusBadge()}
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Status Alerts */}
          {subscriptionStatus?.status === "trialing" &&
            !subscriptionStatus.cancelAtPeriodEnd && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <BeakerIcon className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-blue-800 mb-1">
                      Free Trial Active
                    </h3>
                    <p className="text-sm text-blue-700 mb-2">
                      Your trial ends on{" "}
                      <strong>
                        {formatDate(subscriptionStatus.currentPeriodEnd)}
                      </strong>
                      .{" "}
                      {subscriptionStatus.planName &&
                      subscriptionStatus.planPriceCents != null ? (
                        <>
                          After that, you'll be automatically charged{" "}
                          <strong>
                            $
                            {(subscriptionStatus.planPriceCents / 100).toFixed(
                              0,
                            )}
                            /month
                          </strong>{" "}
                          for the {subscriptionStatus.planName} plan.
                        </>
                      ) : null}
                    </p>
                    <p className="text-sm text-blue-700 mb-3">
                      You have <strong>200 minutes</strong> available during
                      the trial. Want full access now? Upgrade to your plan
                      immediately — you'll be charged today and get your full
                      minute quota right away.
                    </p>
                    <button
                      onClick={handleUpdatePaymentMethod}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-blue-300 text-blue-700 bg-white hover:bg-blue-50"
                    >
                      Upgrade Now / Manage Trial
                    </button>
                  </div>
                </div>
              </div>
            )}

          {subscriptionStatus?.status === "trialing" &&
            subscriptionStatus.cancelAtPeriodEnd && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex">
                  <XCircleIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-800 mb-1">
                      Trial Canceled — No Charge
                    </h3>
                    <p className="text-sm text-gray-600">
                      Your trial has been canceled. You will{" "}
                      <strong>not</strong> be charged. You can continue using
                      the service with your remaining trial minutes until{" "}
                      <strong>
                        {formatDate(subscriptionStatus.currentPeriodEnd)}
                      </strong>
                      , after which your account will be deactivated.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {subscriptionStatus?.status === "past_due" &&
            subscriptionStatus.gracePeriodActive &&
            subscriptionStatus.gracePeriodEnds && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-orange-800 mb-1">
                      Payment Failed - Grace Period Active
                    </h3>
                    <p className="text-sm text-orange-700 mb-2">
                      Your payment could not be processed. You can continue
                      using the service until{" "}
                      <strong>
                        {formatDateTime(subscriptionStatus.gracePeriodEnds)}
                      </strong>
                      .
                    </p>
                    <p className="text-sm text-orange-700">
                      Please update your payment method below to avoid service
                      interruption.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {subscriptionStatus?.status === "past_due" &&
            !subscriptionStatus.gracePeriodActive && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <XCircleIcon className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800 mb-1">
                      Grace Period Expired
                    </h3>
                    <p className="text-sm text-red-700 mb-2">
                      Your grace period has ended. Uploads are now disabled
                      until payment is received.
                    </p>
                    <p className="text-sm text-red-700">
                      Please update your payment method immediately to restore
                      service.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {subscriptionStatus?.status === "canceled" && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <XCircleIcon className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800 mb-1">
                    Subscription Canceled
                  </h3>
                  <p className="text-sm text-red-700">
                    Your subscription has been canceled. Please contact support
                    to reactivate your account.
                  </p>
                </div>
              </div>
            </div>
          )}

          {subscriptionStatus?.status === "active" &&
            !subscriptionStatus.cancelAtPeriodEnd && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800 mb-1">
                      Subscription Active
                    </h3>
                    <p className="text-sm text-green-700">
                      Your subscription is active and in good standing.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {subscriptionStatus?.status === "active" &&
            subscriptionStatus.cancelAtPeriodEnd && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-orange-800 mb-1">
                      Subscription Canceling
                    </h3>
                    <p className="text-sm text-orange-700">
                      Your subscription will end on{" "}
                      <strong>
                        {formatDate(subscriptionStatus.currentPeriodEnd)}
                      </strong>
                      . You'll retain full access until then. To reactivate,
                      use Manage Subscription below.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Billing Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {subscriptionStatus?.status === "trialing"
                  ? "Trial Ends"
                  : "Current Period End"}
              </label>
              <p className="text-sm text-gray-900">
                {subscriptionStatus?.currentPeriodEnd
                  ? formatDate(subscriptionStatus.currentPeriodEnd)
                  : "N/A"}
              </p>
            </div>

            {subscriptionStatus?.paymentFailedAt && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Failed On
                </label>
                <p className="text-sm text-gray-900">
                  {formatDateTime(subscriptionStatus.paymentFailedAt)}
                </p>
              </div>
            )}
          </div>

          {/* Update Payment Button */}
          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={handleUpdatePaymentMethod}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <CreditCardIcon className="h-5 w-5 mr-2" />
              Manage Subscription
            </button>
          </div>
        </div>
      </div>

      {/* Usage Summary Card */}
      {usageInfo && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Usage Summary</h2>
          </div>

          <div className="px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <label className="block text-sm font-medium text-gray-700">
                    Minutes Used
                  </label>
                </div>
                <p className="mt-2 text-2xl font-semibold text-gray-900">
                  {Math.floor(
                    usageInfo.seconds_used_current_period / 60,
                  ).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">
                  of{" "}
                  {Math.floor(
                    usageInfo.monthly_minute_limit / 60,
                  ).toLocaleString()}{" "}
                  minutes
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Usage Percentage
                </label>
                <div className="mt-2">
                  <div className="flex items-baseline">
                    <p className="text-2xl font-semibold text-gray-900">
                      {Number(usageInfo.usage_percentage).toFixed(1)}%
                    </p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{
                        width: `${Math.min(usageInfo.usage_percentage, 100)}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minutes Remaining
                </label>
                <p className="mt-2 text-2xl font-semibold text-gray-900">
                  {Math.floor(
                    usageInfo.remaining_seconds / 60,
                  ).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">
                  Resets {formatDate(usageInfo.current_period_end)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Information Card */}
      <div className="bg-white shadow rounded-lg mt-6">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Account Information
          </h2>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Owner
            </label>
            <p className="text-sm text-gray-900">
              {user?.firstName} {user?.lastName}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <p className="text-sm text-gray-900">{user?.email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <p className="text-sm text-gray-900">{user?.company}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;
